import { getCellValuesByFieldType } from 'components/Workspaces/Batch/ExportExcel/fieldValuesBuilder';
import { getBatchFieldValuesForExport } from 'components/Workspaces/Batch/ExportExcel/fieldValuesMapper';
import type { SPIntl } from 'contexts/intl/intlContext';
import type { Locale } from 'date-fns';
import type { FieldMetadata } from 'models/forms/fields';
import type { FormDataStateMap } from 'state/formData';
import type { ProductGraph, ProductNode } from 'state/product-description/model';
import {
  getBatchFormFieldMetadata,
  getBatchFormMustForceAdvancedMode,
  getBatchResult,
  getFormEnrichedData,
  getFormsEnrichedProductVariations,
  getMergedProductDescription,
  getSelectedFormIdsInBatchTab,
  getUserPreferencesBatchWorkspaceVersion,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import { addOrReplaceKey } from 'state/utils/collection';

// These composite sections still need dedicated export shaping before they can be written safely.
const BATCH_EXPORT_UNSUPPORTED_FIELD_IDS = new Set([
  'DealSplit',
  'DealSplitTab',
  'FeesAndMetrics',
  'PaymentDatesCalendar',
  'ScheduleDates',
  'ScheduleDatesExportGridButton',
  'ScheduleDatesFooter',
  'ScheduleDatesModal',
  'ScheduleDatesSettings',
  'ScheduleDatesTab',
  'ScheduleDatesTopBar',
  'ValuationDatesCalendar',
]);

function isBatchFieldSupportedForExport(fieldId: string): boolean {
  return !BATCH_EXPORT_UNSUPPORTED_FIELD_IDS.has(fieldId);
}

function collectBatchHeaderFields(productGraph: ProductGraph): ProductNode[] {
  const headers: ProductNode[] = [];

  const visitNode = (node: ProductNode): void => {
    if (!isBatchFieldSupportedForExport(node.id)) {
      return;
    }

    if (node.type === undefined && node.children) {
      node.children.forEach(visitNode);
      return;
    }

    headers.push(node);
  };

  productGraph.forEach(node => {
    node.children?.forEach(visitNode);
  });

  return headers;
}

function getExportableBatchMetadata(metadata: Record<string, FieldMetadata>): Record<string, FieldMetadata> {
  const exportableMetadata: Record<string, FieldMetadata> = {};

  Object.values(metadata).forEach(fieldMetadata => {
    if (isBatchFieldSupportedForExport(fieldMetadata.id)) {
      exportableMetadata[fieldMetadata.id] = fieldMetadata;
    }
  });

  return exportableMetadata;
}

export function getBatchFieldValuesData(
  state: AppState,
  tabId: string,
  intl: SPIntl,
  locale: Locale,
): Record<string, any> {
  const formIds = getSelectedFormIdsInBatchTab(state, tabId);
  const productNodes = getBatchFieldsForHeader(state, formIds);
  const formDataMap = getBatchValues(state, formIds, intl, locale);
  return getBatchFieldValuesForExport(formDataMap, productNodes, intl, state);
}

export function getBatchFieldsForHeader(state: AppState, formIds: string[]): ProductNode[] {
  const productVariations = getFormsEnrichedProductVariations(state, formIds);
  const allFieldsMetadata = formIds.map(formId => getBatchFormFieldMetadata(state, formId));
  const batchWorkspaceVersion = getUserPreferencesBatchWorkspaceVersion(state);
  const mustForceAdvancedMode = getBatchFormMustForceAdvancedMode(state, formIds);
  const productGraph = getMergedProductDescription(
    state,
    'Batch',
    productVariations,
    allFieldsMetadata,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );
  const headers = collectBatchHeaderFields(productGraph);

  const createProductNode = (id: string, translationKey: string): ProductNode => ({
    id,
    translationKey,
  });

  const additionalHeaders = [
    createProductNode('FormStatus', 'FormStatus'),
    createProductNode('ProductType', 'ProductType'),
    createProductNode('ProductSubtype', 'ProductSubtype'),
    createProductNode('Wrapper', 'Wrapper'),
  ];
  const additionalHeaderIds = new Set(additionalHeaders.map(({ id }) => id));

  const sortedHeaders = [
    ...headers.filter(({ id }) => id === 'QuoteExternalId'),
    ...additionalHeaders,
    ...headers.filter(({ id }) => id !== 'QuoteExternalId' && !additionalHeaderIds.has(id)),
  ];
  return sortedHeaders;
}

function getBatchValues(state: AppState, formIds: string[], intl: SPIntl, locale: Locale): FormDataStateMap {
  let formDataMap: FormDataStateMap = {};
  formIds.forEach(formId => {
    const metadata = getBatchFormFieldMetadata(state, formId);
    const exportableMetadata = getExportableBatchMetadata(metadata);
    const enrichedData = getFormEnrichedData(state, formId);
    const batchResult = getBatchResult(state, formId);
    const fullFieldValues = { ...enrichedData, ...batchResult };
    const formattedValue = getCellValuesByFieldType(exportableMetadata, fullFieldValues, intl, locale);
    formDataMap = addOrReplaceKey(formDataMap, formId, formattedValue);
  });
  return formDataMap;
}
